"use client";

import { c as _c } from "react/compiler-runtime";
import { AlertCircle, Download, Film } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { memo, useEffect, useState, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { videoStudioSuggestions, sampleSuggestions } from "@/lib/hero-suggestions";
import { downloadVideo } from "@/lib/video-gen";
import type { VideoGalleryItem } from "@/lib/video-gen";
const VIDEO_EXPIRY_MS = 24 * 60 * 60 * 1000;
const EXPIRY_WARN_THRESHOLD_S = 60 * 60;
function isExpiredByTime(expiresAt: number | null, timestamp: number): boolean {
  if (expiresAt !== null) {
    return Math.floor(Date.now() / 1000) > expiresAt;
  }
  return Date.now() - timestamp > VIDEO_EXPIRY_MS;
}
function isExpiringSoon(expiresAt: number | null): boolean {
  if (expiresAt === null) {
    return false;
  }
  const secondsLeft = expiresAt - Math.floor(Date.now() / 1000);
  return secondsLeft > 0 && secondsLeft < EXPIRY_WARN_THRESHOLD_S;
}
function formatTimeUntilExpiry(expiresAt: number): string {
  const secondsLeft = expiresAt - Math.floor(Date.now() / 1000);
  if (secondsLeft < 60) {
    return "less than a minute";
  }
  if (secondsLeft < 3600) {
    return `${Math.floor(secondsLeft / 60)} min`;
  }
  return `${Math.floor(secondsLeft / 3600)} h`;
}
interface VideoGalleryProps {
  items: VideoGalleryItem[];
  comparisonMode: boolean;
  onSuggestionClick?: (prompt: string) => void;
}
const VideoPlayer = memo(t0 => {
  const $ = _c(22);
  const {
    url,
    modelName,
    expiresAt,
    onLoadError
  } = t0;
  let t1;
  if ($[0] !== onLoadError) {
    t1 = () => {
      onLoadError();
    };
    $[0] = onLoadError;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleError = t1;
  const t2 = expiresAt ?? null;
  let t3;
  if ($[2] !== t2) {
    t3 = isExpiringSoon(t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const expiringSoon = t3;
  let t4;
  if ($[4] !== handleError || $[5] !== url) {
    t4 = <video src={url} controls={true} autoPlay={true} loop={true} playsInline={true} className="w-full rounded-lg" onError={handleError} />;
    $[4] = handleError;
    $[5] = url;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== url) {
    t5 = () => downloadVideo(url);
    $[7] = url;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Download className="h-4 w-4" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5) {
    t7 = <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"><Button variant="secondary" size="icon" className="h-8 w-8 bg-background/80 backdrop-blur-sm" onClick={t5}>{t6}</Button></div>;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== modelName) {
    t8 = modelName && <div className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity"><Badge variant="secondary" className="bg-background/80 backdrop-blur-sm text-xs">{modelName}</Badge></div>;
    $[12] = modelName;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== expiresAt || $[15] !== expiringSoon) {
    t9 = expiringSoon && expiresAt && <div className="absolute top-2 left-2"><Badge variant="secondary" className="bg-amber-500/90 text-white text-xs backdrop-blur-sm">Expires in {formatTimeUntilExpiry(expiresAt)}</Badge></div>;
    $[14] = expiresAt;
    $[15] = expiringSoon;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t4 || $[18] !== t7 || $[19] !== t8 || $[20] !== t9) {
    t10 = <div className="group relative overflow-hidden rounded-lg border">{t4}{t7}{t8}{t9}</div>;
    $[17] = t4;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
});
function VideoProgress(t0) {
  const $ = _c(8);
  const {
    status,
    progress
  } = t0;
  const progressValue = progress ?? 0;
  const label = status === "queued" ? "Queued..." : `Generating... ${progressValue}%`;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Film className="h-10 w-10 text-muted-foreground/50 animate-pulse" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== progressValue) {
    t2 = <Progress value={progressValue} className="h-2" />;
    $[1] = progressValue;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== label) {
    t3 = <p className="text-sm text-muted-foreground text-center">{label}</p>;
    $[3] = label;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <div className="relative overflow-hidden rounded-lg border bg-muted/30"><div className="flex flex-col items-center justify-center py-16 px-4 gap-4">{t1}<div className="w-full max-w-xs space-y-2">{t2}{t3}</div></div></div>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function EmptyState(t0) {
  const $ = _c(10);
  const {
    onSuggestionClick
  } = t0;
  const [suggestions, setSuggestions] = useState(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setSuggestions(sampleSuggestions(videoStudioSuggestions, 6));
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Film className="h-16 w-16 text-muted-foreground/30 mb-6" />;
    t4 = <h3 className="text-lg font-medium mb-2">No videos yet</h3>;
    t5 = <p className="text-sm text-muted-foreground mb-8 max-w-md">Describe the video you want to create and click Generate to get started.</p>;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== onSuggestionClick || $[6] !== suggestions) {
    t6 = suggestions ? <motion.div key="suggestions" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.07,
      ease: "easeOut"
    }} className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl w-full">{suggestions.map((s, index) => <motion.button key={s} type="button" initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.12,
        delay: index * 0.025,
        ease: "easeOut"
      }} onClick={() => onSuggestionClick?.(s)} className="rounded-md border px-4 py-3 text-left text-sm hover:bg-muted/60 transition-colors">{s}</motion.button>)}</motion.div> : null;
    $[5] = onSuggestionClick;
    $[6] = suggestions;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <div className="flex flex-col items-center justify-center py-20 text-center">{t3}{t4}{t5}<AnimatePresence>{t6}</AnimatePresence></div>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
}
function VideoInputThumbnails(t0) {
  const $ = _c(5);
  const {
    item
  } = t0;
  let images;
  if ($[0] !== item.frameInputs || $[1] !== item.referenceImages) {
    images = [];
    if (item.frameInputs?.start) {
      images.push({
        src: item.frameInputs.start.dataUrl,
        label: "First frame"
      });
    }
    if (item.frameInputs?.end) {
      images.push({
        src: item.frameInputs.end.dataUrl,
        label: "Last frame"
      });
    }
    if (item.referenceImages) {
      item.referenceImages.forEach((ref, i) => {
        images.push({
          src: ref.dataUrl,
          label: `Reference ${i + 1}`
        });
      });
    }
    $[0] = item.frameInputs;
    $[1] = item.referenceImages;
    $[2] = images;
  } else {
    images = $[2];
  }
  if (images.length === 0) {
    return null;
  }
  let t1;
  if ($[3] !== images) {
    t1 = <div className="flex gap-1 shrink-0">{images.map(_temp)}</div>;
    $[3] = images;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function _temp(img, i_0) {
  return <img key={i_0} src={img.src} alt={img.label} title={img.label} className="h-6 w-6 rounded border object-cover" />;
}
function SingleModeItem(t0) {
  const $ = _c(26);
  const {
    item
  } = t0;
  const model = item.models[0];
  const [loadError, setLoadError] = useState(false);
  if (!model) {
    return null;
  }
  let t1;
  if ($[0] !== item.timestamp || $[1] !== model.expiresAt) {
    t1 = isExpiredByTime(model.expiresAt, item.timestamp);
    $[0] = item.timestamp;
    $[1] = model.expiresAt;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const expired = t1;
  const unavailable = loadError && !expired;
  let t2;
  if ($[3] !== item) {
    t2 = <VideoInputThumbnails item={item} />;
    $[3] = item;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== item.prompt) {
    t3 = <p className="text-sm text-muted-foreground truncate flex-1">{item.prompt}</p>;
    $[5] = item.prompt;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== item.timestamp) {
    t4 = new Date(item.timestamp).toLocaleTimeString();
    $[7] = item.timestamp;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <span className="text-xs text-muted-foreground shrink-0">{t4}</span>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t5) {
    t6 = <div className="flex items-center gap-2">{t2}{t3}{t5}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== expired || $[16] !== model.error || $[17] !== model.expiresAt || $[18] !== model.isLoading || $[19] !== model.job || $[20] !== model.videoUrl || $[21] !== unavailable) {
    t7 = model.error ? <div className="flex items-center gap-2 p-4 rounded-lg border border-destructive/50 bg-destructive/5"><AlertCircle className="h-4 w-4 text-destructive shrink-0" /><p className="text-sm text-destructive">{model.error}</p></div> : model.isLoading && model.job ? <div className="max-w-lg"><VideoProgress status={model.job.status} progress={model.job.progress} /></div> : model.isLoading ? <Skeleton className="h-64 max-w-lg rounded-lg" /> : model.videoUrl ? <div className="max-w-lg">{expired ? <div className="flex items-center justify-center h-32 rounded-lg border bg-muted/30 text-sm text-muted-foreground">Video expired</div> : unavailable ? <div className="flex flex-col items-center justify-center h-32 rounded-lg border bg-muted/30 gap-1"><p className="text-sm text-muted-foreground">Video unavailable</p><p className="text-xs text-muted-foreground/70">The video may no longer be available from the provider</p></div> : <VideoPlayer url={model.videoUrl} expiresAt={model.expiresAt} onLoadError={() => setLoadError(true)} />}</div> : null;
    $[15] = expired;
    $[16] = model.error;
    $[17] = model.expiresAt;
    $[18] = model.isLoading;
    $[19] = model.job;
    $[20] = model.videoUrl;
    $[21] = unavailable;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t6 || $[24] !== t7) {
    t8 = <div className="space-y-2">{t6}{t7}</div>;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
}
function ComparisonModeItem(t0) {
  const $ = _c(27);
  const {
    item
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [loadErrorModels, setLoadErrorModels] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = modelId => {
      setLoadErrorModels(prev => new Set(Array.from(prev).concat(modelId)));
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleLoadError = t2;
  let t3;
  if ($[2] !== item) {
    t3 = <VideoInputThumbnails item={item} />;
    $[2] = item;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== item.prompt) {
    t4 = <p className="text-sm text-muted-foreground truncate flex-1">{item.prompt}</p>;
    $[4] = item.prompt;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== item.timestamp) {
    t5 = new Date(item.timestamp).toLocaleTimeString();
    $[6] = item.timestamp;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <span className="text-xs text-muted-foreground shrink-0">{t5}</span>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t6) {
    t7 = <div className="flex items-center gap-2">{t3}{t4}{t6}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = `grid gap-4 ${item.models.length === 1 ? "grid-cols-1" : item.models.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`;
  let t9;
  if ($[14] !== item.models || $[15] !== item.timestamp || $[16] !== loadErrorModels) {
    let t10;
    if ($[18] !== item.timestamp || $[19] !== loadErrorModels) {
      t10 = model => {
        const expired = isExpiredByTime(model.expiresAt, item.timestamp);
        const unavailable = loadErrorModels.has(model.modelId) && !expired;
        return <div key={model.modelId} className="space-y-2"><Badge variant="outline" className="text-xs">{model.modelName}</Badge>{model.error ? <div className="flex items-center gap-2 p-4 rounded-lg border border-destructive/50 bg-destructive/5"><AlertCircle className="h-4 w-4 text-destructive shrink-0" /><p className="text-sm text-destructive">{model.error}</p></div> : model.isLoading && model.job ? <VideoProgress status={model.job.status} progress={model.job.progress} /> : model.isLoading ? <Skeleton className="h-64 rounded-lg" /> : model.videoUrl ? expired ? <div className="flex items-center justify-center h-32 rounded-lg border bg-muted/30 text-sm text-muted-foreground">Video expired</div> : unavailable ? <div className="flex flex-col items-center justify-center h-32 rounded-lg border bg-muted/30 gap-1"><p className="text-sm text-muted-foreground">Video unavailable</p><p className="text-xs text-muted-foreground/70">The video may no longer be available from the provider</p></div> : <VideoPlayer url={model.videoUrl} modelName={model.modelName} expiresAt={model.expiresAt} onLoadError={() => handleLoadError(model.modelId)} /> : null}</div>;
      };
      $[18] = item.timestamp;
      $[19] = loadErrorModels;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    t9 = item.models.map(t10);
    $[14] = item.models;
    $[15] = item.timestamp;
    $[16] = loadErrorModels;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[21] !== t8 || $[22] !== t9) {
    t10 = <div className={t8}>{t9}</div>;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t7) {
    t11 = <div className="space-y-2">{t7}{t10}</div>;
    $[24] = t10;
    $[25] = t7;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}
export function VideoGallery(t0) {
  const $ = _c(9);
  const {
    items,
    comparisonMode,
    onSuggestionClick
  } = t0;
  if (items.length === 0) {
    let t1;
    if ($[0] !== onSuggestionClick) {
      t1 = <EmptyState onSuggestionClick={onSuggestionClick} />;
      $[0] = onSuggestionClick;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== comparisonMode || $[3] !== items) {
    let t2;
    if ($[5] !== comparisonMode) {
      t2 = item => <div key={item.id} id={`gallery-${item.id}`}>{comparisonMode ? <ComparisonModeItem item={item} /> : <SingleModeItem item={item} />}</div>;
      $[5] = comparisonMode;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = items.map(t2);
    $[2] = comparisonMode;
    $[3] = items;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <div className="space-y-8">{t1}</div>;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}